import { useState, useEffect, useMemo} from 'react';
import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import './chartDisplay.css';
import Box from '@mui/material/Box';
import { Button, capitalize } from '@mui/material';

import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton'; 
import ListItemText from '@mui/material/ListItemText';
import { useHCF, useRockbash, useRenewableForecast, useShortTermForecast, useHistoricalForecast, useRevenue, useWeeklyPeakLoadForecast, useRevenueMonthly, useRockbashAccuracy, useRockbashAccuracyTimeSeries, useMarginalCost, useBidData, useMEConfig, useUserRole, useCurrentSupplyDemand } from '../api/GetData';

function rollingAverage(data, windowSize = 7) {
    const result = [];
    for (let i = 0; i < data.length; i++) {
        // Slice safely: if i < windowSize, take only the available points
        const start = Math.max(0, i - windowSize + 1);
        const window = data.slice(start, i + 1);
        const avg = window.reduce((sum, val) => sum + val, 0) / window.length;
        result.push(avg);
    }
    return result;
}

function getCurrentHourPower(HCFdata) {
    if (!HCFdata) return [];

    const categories = Object.keys(HCFdata).filter(
        category => HCFdata[category] && HCFdata[category].length > 0
    );

    const now = new Date();

    const pieChartData = categories.map(category => {
        const dataPoints = HCFdata[category] || [];

        const currentPoint = dataPoints.find(point => {
        const pointDate = new Date(point.datetime);
        return (
            pointDate.getUTCFullYear() === now.getUTCFullYear() &&
            pointDate.getUTCMonth() === now.getUTCMonth() &&
            pointDate.getUTCDate() === now.getUTCDate() &&
            pointDate.getUTCHours() === now.getUTCHours()
        );
        });

        const powerValue = currentPoint ? Math.round(parseFloat(currentPoint.power)) : 0;

        return {
        label: category,
        value: powerValue
        };
    }).filter(item => item.value > 0);  // Optional: remove slices with 0

    return pieChartData;
    }

function toChartNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function pickFirstChartNumber(row, keys = []) {
    for (const key of keys) {
        const value = toChartNumber(row?.[key]);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

function buildOptionalSeries(rows, configs, showMark) {
    if (!Array.isArray(rows)) return [];
    return configs
        .filter((config) =>
            rows.some((row) => pickFirstChartNumber(row, config.keys) !== null)
        )
        .map((config) => ({
            data: rows.map((row) => pickFirstChartNumber(row, config.keys)),
            label: config.label,
            color: config.color,
            showMark,
        }));
}

function buildPoolPriceModelSeries(rows, showMark) {
    return buildOptionalSeries(
        rows,
        [
            {
                keys: ['rf_24h_specialists_forecast_price'],
                label: 'WiSDM Forecast Price ($)',
                color: '#2196f3',
            },
        ],
        showMark
    );
}

function buildStorePoolPriceModelSeries(rows, showMark) {
    return buildOptionalSeries(
        rows,
        [
            {
                keys: ['rf_24h_specialists_store_forecast_price'],
                label: 'WiSDM Forecast Price ($)',
                color: '#2196f3',
            },
        ],
        showMark
    );
}

function buildRockbashModelSeries(rows, showMark) {
    return buildOptionalSeries(
        rows,
        [
            {
                keys: ['forecastplus_price'],
                label: 'WISDM Forecast',
                color: '#2196f3',
            },
            {
                keys: ['aeso_forecast_price'],
                label: 'AESO Forecast',
                color: '#545454',
            },
            {
                keys: ['extra_trees_refined_forecast_price'],
                label: 'Extra Trees Refined',
                color: '#2f855a',
            },
            {
                keys: ['rf_refined_forecast_price'],
                label: 'RF Refined',
                color: '#7c3aed',
            },
            {
                keys: ['zoo_extra_trees_forecast_price', 'forecast_price'],
                label: 'Zoo Extra Trees',
                color: '#f59e0b',
            },
            {
                keys: ['zoo_rf_forecast_price'],
                label: 'Zoo RF',
                color: '#d97706',
            },
            {
                keys: ['forecast'],
                label: 'H10 Model',
                color: '#4681E1',
            },
        ],
        showMark
    );
}

function toChartDate(value) {
    if (!value) return null;
    const parsed = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDateBounds(rows) {
    const dates = (Array.isArray(rows) ? rows : [])
        .map((row) => toChartDate(row?.datetime))
        .filter((date) => date !== null);
    return {
        dates,
        min: dates.length > 0 ? dates[0] : new Date(0),
        max: dates.length > 0 ? dates[dates.length - 1] : new Date(0),
    };
}

function getMonthOffset(date, referenceDate = new Date()) {
    return (
        (date.getFullYear() - referenceDate.getFullYear()) * 12 +
        (date.getMonth() - referenceDate.getMonth())
    );
}

function toMeteredVolumeValue(row) {
    const meteredVolume = Number.parseFloat(
        row?.metered_volume ?? row?.TNG ?? row?.tng ?? 0
    );
    return Number.isFinite(meteredVolume) ? meteredVolume : 0;
}

function toPoolPriceValue(row) {
    const poolPrice = Number.parseFloat(
        row?.poolprice ?? row?.pool_price ?? row?.poolPrice ?? 0
    );
    return Number.isFinite(poolPrice) ? poolPrice : 0;
}

function buildRevenueChartRows(rows) {
    const referenceDate = new Date();
    return (Array.isArray(rows) ? rows : [])
        .map((row) => {
            const datetime = toChartDate(row?.datetime);
            if (!datetime) return null;

            const monthOffset = getMonthOffset(datetime, referenceDate);
            const meteredVolume = toMeteredVolumeValue(row);
            const revenue = Math.max(meteredVolume * toPoolPriceValue(row), 0);

            return {
                datetime,
                meteredVolume,
                revenue,
                isCurrentMonth: monthOffset === 0,
                isPreviousMonth: monthOffset === -1,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.datetime - b.datetime);
}

function getMonthLabels(referenceDate = new Date()) {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
    const currentMonth = formatter.format(referenceDate);
    const previousDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);
    const previousMonth = formatter.format(previousDate);
    return { currentMonth, previousMonth };
}

export const ChartDisplayThree = ({set=[0,0], selectedCharts, plantName}) => {
    const List1 = ["Current Metered Volume","Forecasted Demand"];
    const List2 = ["Current Supply","Long Term Supply"];
    const {data: userProfile} = useUserRole()
    const role = (userProfile || '').toLowerCase()

    const {data: renewabledataConfig} = useRenewableForecast();
    useEffect(()=>{
        if(role=="dev"){
            if(renewabledataConfig!==undefined){
            setRenewableData(renewabledataConfig)
        }
        }
    },[renewabledataConfig,role])
    const [renewabledata, setRenewableData] = useState(null)
    const { isPending, isError, data: timeSeriesAccuracyData, error } = useRockbashAccuracy();
    const { data: accuracyDataConfig, isLoading: accuracyLoading, error: accuracyError } = useRockbashAccuracyTimeSeries();
    const [accuracyData, setAccuracyData] = useState(null)
    useEffect(()=>{
        if(role=="dev"){
            if(accuracyDataConfig!==undefined){
                setAccuracyData(accuracyDataConfig)
            }
        }
    },[accuracyDataConfig,role])



    

    const {data: stfdata} = useShortTermForecast();
    const {data: hdata} = useHistoricalForecast();
    const {data: rdata} = useRevenue(plantName);
    const {data: wdata} = useWeeklyPeakLoadForecast();
    const {data: rmdata} = useRevenueMonthly();
    const {data: rbdata} = useRockbash();
    
    const {data: marginalCost} = useMarginalCost(plantName);
    const {data: HCFdata} = useHCF()
    const {data: biddata} = useBidData(plantName)
    const {data: MEConfig} = useMEConfig(plantName)
    const marginalCostValue = Number(MEConfig?.marginal_cost ?? 0)
    const assetIdentifier = MEConfig?.asset_identifier ?? '';
    const assetName = MEConfig?.asset_name ?? '';
    const csdAssetKey = assetName && assetIdentifier ? `${assetName} (${assetIdentifier})` : '';
    const { data: supplyDemandRaw } = useCurrentSupplyDemand(csdAssetKey);
    const supplyDemandRows = Array.isArray(supplyDemandRaw) ? supplyDemandRaw : [];
    const supplyDemandTimeline = supplyDemandRows.map(row => toChartDate(row?.datetime)).filter(Boolean);
    const tngValues = supplyDemandRows.map(row => toChartNumber(row?.TNG ?? row?.tng ?? row?.metered_volume ?? row?.supply ?? null));
    const csdYMax = tngValues.filter(v => v !== null).length > 0 ? Math.ceil(Math.max(1, ...tngValues.filter(v => v !== null)) * 1.1) : 1;
    const csdMinDate = supplyDemandTimeline.length > 0 ? supplyDemandTimeline[0] : new Date(0);
    const csdMaxDate = supplyDemandTimeline.length > 0 ? supplyDemandTimeline[supplyDemandTimeline.length - 1] : new Date(0);


    const [anchorEl1, setAnchorEl1] = useState(null);
    const [anchorEl2, setAnchorEl2] = useState(null);
    const [selectedItem1, setSelectedItem1] = useState(set[0]);
    const [selectedItem2, setSelectedItem2] = useState(set[1]);
    const [name1, setName1] = useState(List1[set[0]]);
    const [name2, setName2] = useState(List2[set[1]]);
    const [accuracyHistogram, setAccuracyHistogram] = useState([]);
    // Placeholder 24-hour values (for now same as above)
    const[datetimeAccuracy, setDatetimeAccuracy] = useState([]);
    const [accuracy24h, setAccuracy24h] = useState([]);
    const [bidAccuracy24h, setBidAccuracy24h] = useState([]);
   
    const [chartData, setChartData] = useState([]);
    const plantLabel = plantName ? String(plantName) : "WLC1";
    const plantLabelUpper = plantLabel.toUpperCase() === "WLC1" ? "WLC1" : plantLabel;
    const parseRevenueData = (raw) => {
        if (!raw) return null;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
            try {
                return JSON.parse(raw);
            } catch (error) {
                console.error("Failed to parse revenue data:", error);
                return null;
            }
        }
        return null;
    };
    const revenueData = parseRevenueData(rdata);
    const revenueChartRows = buildRevenueChartRows(revenueData);
    const revenueTimeline = revenueChartRows.map((row) => row.datetime);
    const revenueMinDate = revenueTimeline.length > 0 ? revenueTimeline[0] : new Date(0);
    const revenueMaxDate =
        revenueTimeline.length > 0
            ? revenueTimeline[revenueTimeline.length - 1]
            : new Date(0);
    const meteredVolumeSeries = revenueChartRows.map((row) => row.meteredVolume);
    const previousMonthRevenueSeries = revenueChartRows.map((row) =>
        row.isPreviousMonth ? row.revenue : null
    );
    const currentMonthRevenueSeries = revenueChartRows.map((row) =>
        row.isCurrentMonth ? row.revenue : null
    );
    const { currentMonth, previousMonth } = getMonthLabels();
    const parseBidData = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
                if (Array.isArray(parsed?.BidData)) return parsed.BidData;
            } catch (error) {
                console.error("Failed to parse bid data:", error);
            }
            return [];
        }
        if (typeof raw === "object") {
            if (Array.isArray(raw.body)) return raw.body;
            if (typeof raw.body === "string") {
                try {
                    const parsed = JSON.parse(raw.body);
                    if (Array.isArray(parsed)) return parsed;
                    if (Array.isArray(parsed?.BidData)) return parsed.BidData;
                } catch (error) {
                    console.error("Failed to parse bid data body:", error);
                }
            }
            if (Array.isArray(raw.BidData)) return raw.BidData;
            if (Array.isArray(raw.data)) return raw.data;
        }
        return [];
    };
    const bidPoints = parseBidData(biddata);
    const bidChartPoints = bidPoints
        .map((item) => {
            let timeValue = null;
            let dataValue = null;
            if (Array.isArray(item)) {
                timeValue = item[0];
                dataValue = item[3];
            } else if (item && typeof item === "object") {
                timeValue = item.datetime ?? item.timestamp ?? item.time ?? item.date;
                dataValue = item.expected_dispatch ?? item.expectedDispatch ?? item.dispatch ?? item.mw ?? item.value ?? item.offer ?? item.quantity;
            }
            const time = new Date(timeValue ?? 0);
            const value = Number(dataValue ?? 0);
            if (Number.isNaN(time.getTime()) || Number.isNaN(value)) {
                return null;
            }
            return { time, value };
        })
        .filter(Boolean);
    const buildBidFallbackPoints = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return [
            { time: today, value: 0 },
            { time: tomorrow, value: 0 },
        ];
    };
    const bidChartSeriesPoints = bidChartPoints.length > 0
        ? bidChartPoints
        : buildBidFallbackPoints();
    const rollingAccuracy = rollingAverage([...accuracy24h].reverse(), 7);
    const rollingBidAccuracy = rollingAverage([...bidAccuracy24h].reverse(), 7);
    const pieChartData = getCurrentHourPower(HCFdata);
    useEffect(()=>{
        console.log(biddata)
    },[biddata])
 // Pie Chart Formatting
    const categories = HCFdata 
    ? Object.keys(HCFdata).filter(
        category => HCFdata[category] && HCFdata[category].length > 0
        )
    : [];
    const getColorForCategory = (category) => { // the colors for each of the categories in the hourly capability chart
        const colors = {
          'CC': '#212121',
          'GFS': '#4681E1',
          'ENERGY STORAGE': '#545454',
          'HYDRO': '#6978A2',
          'WIND': '#3a3a3a',
          'SOLAR': '#2E8BC0',
          'SC': '#011f4b',
          'Cogen': '#6497b1'
          
        };
        return colors[category] || '#5dd0ff'; // Default color if category not found
    }
    const minCategoryLength = HCFdata && categories.length > 0
        ? Math.min(...categories.map(c => HCFdata[c]?.length || 0))
        : 0;
    const shortestCategory = HCFdata && categories.length > 0
        ? categories.reduce((a, b) =>
            (HCFdata[a]?.length || 0) < (HCFdata[b]?.length || 0) ? a : b
        )
        : null;

    const getCategorySeries = (category) => {
        const data = HCFdata !== null ? HCFdata[category]: [];
        if (data && data.length > 0) {
          return {
            id: category.toLowerCase().replace(/\s/g, '-'),
            label: category.replace(/_/g, ' '),
            labelStyle: {
              fontSize: '15px',
              fontFamily: 'Tahoma',
              letterSpacing: '0.1em',
              color: getColorForCategory(category),
            },
            data: data.slice(0, minCategoryLength).map(value => parseInt(value['power'])),
            stack: 'total',
            area: true,
            showMark: false,
            color: getColorForCategory(category),
          };
        }
        return null;
      };

    const xAxisData =
    shortestCategory && HCFdata
        ? (HCFdata[shortestCategory] || [])
            .map(d => (d && d.datetime ? new Date(d.datetime) : null))
            .filter(d => d !== null)
        : [];
    const series = categories !== null ? categories.map(getCategorySeries).filter(series => series !== null) : null;

    useEffect(() => {
        if (accuracyData && Array.isArray(accuracyData)&&role=="dev") {
            const bins = Array(15).fill(0);
            accuracyData.forEach(item => {
                if (item.accuracy !== undefined) {
                    const idx = Math.min(14, Math.floor(item.accuracy / 20));
                    bins[idx]++;
                }
            });
            setAccuracyHistogram(bins.map((count, i) => {
                const lower = i * 20;
                const upper = (i + 1) * 20;
                return { range: i === 14 ? `${lower}%+` : `${lower}-${upper}%`, count };
            }));
        }
    }, [accuracyData,userProfile]);

    
useEffect(() => {
    if (timeSeriesAccuracyData && Array.isArray(timeSeriesAccuracyData)&&role=="dev") {
        // Ensure every item is defined and has a datetime
        const validData = timeSeriesAccuracyData.filter(item => item && item.datetime);
        const newDatetimeAccuracy = validData.map(item => new Date(item.datetime))
        const new24hAvgAccuracy = validData.map(item => item.avg_accuracy_24h ?? null)
        const new24hAvgAccuracyBid = validData.map(item => item.bid_accuracy_true_24h ?? null)
        setDatetimeAccuracy((prev) => {
            let list = [...prev];
            list = newDatetimeAccuracy;
            return list;
        });
        setAccuracy24h(new24hAvgAccuracy);
        setBidAccuracy24h(new24hAvgAccuracyBid);
        
    }
}, [timeSeriesAccuracyData,userProfile]);


useEffect(()=>{ //why aren't we just using rmdata why use a state variable
    const plantFilter = (entry) => {
      if (!plantName) return true;
      const asset = String(entry?.asset ?? "").toLowerCase();
      const plant = String(plantName).toLowerCase();
      return asset.includes(plant);
    };
    const nextChartData = (Array.isArray(rmdata) ? rmdata.filter(plantFilter) : [])
      .filter(entry => entry && entry.datetime && entry.wisdm_revenue)
      .map(entry => ({
        datetime: new Date(entry.datetime),
        wisdm_revenue: parseFloat(entry.wisdm_revenue),
        customer_revenue: parseFloat(entry.customer_revenue),
        asset: entry.asset
      }))
      .sort((a, b) => a.datetime - b.datetime);
    setChartData(nextChartData)
},[rmdata, plantName])

    
    const handleClick1 = (event, item) => {
        setAnchorEl1(event.currentTarget);  
    };
    const handleClick3 = (event, item) => {
        setSelectedItem1(item);   
        setAnchorEl1(null); 
        if(item === 0){
            setName1("Current Metered Volume")
        }else{
            setName1("Forecasted Demand")
        }
    };
    
    const handleClose1 = () => {
        setAnchorEl1(null);
    };

    const handleClick2 = (event, item) => {
        setAnchorEl2(event.currentTarget);
    };
    const handleClick4 = (event, item) => {
        setSelectedItem2(item);   
        setAnchorEl2(null); 
        if(item === 0){
            setName2("Current Supply")
        }else{
            setName2("Long Term Supply")
        }
    };
    const handleClose2 = () => {
        setAnchorEl2(null);
    };
    const open2 = Boolean(anchorEl2);
    const id2 = open2 ? 'simple-popover-2' : undefined;
    const open1 = Boolean(anchorEl1);
    const id1 = open1 ? 'simple-popover-1' : undefined;

    const demandSeries = Array.isArray(wdata)
      ? [...wdata]
          .filter((row) => row && row.datetime)
          .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      : [];
    const stfDateBounds = getDateBounds(stfdata);
    const stfStoreData = useMemo(() => {
        if (!Array.isArray(stfdata)) return [];
        const cutoff = new Date(Date.now() + 8 * 60 * 60 * 1000);
        return stfdata.filter(row => {
            const dt = toChartDate(row?.datetime);
            return dt && dt <= cutoff;
        });
    }, [stfdata]);
    const stfStoreDateBounds = getDateBounds(stfStoreData);
    const hdataDateBounds = getDateBounds(hdata);
    const rbdataDateBounds = getDateBounds(rbdata);
    const hcfDateBounds = {
        min: xAxisData.length > 0 ? xAxisData[0] : new Date(0),
        max: xAxisData.length > 0 ? xAxisData[xAxisData.length - 1] : new Date(0),
    };



    return(
            <Box className="graph-grid">
            {selectedCharts.short_forecast && (
                <Box className='graph'> {/*Small Forecast*/}
                <p className='Title'>Short Term Forecast</p>
                <Box className='graph-inner'>
                <LineChart
                margin={{ left: 80}}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '10px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        padding: 1,
                    },

                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HE)',
                    scaleType: 'time',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: stfdata !== undefined ? stfdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: stfDateBounds.min, // Start date
                    max: stfDateBounds.max, // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Pool Price (CAD $)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: undefined,
                }]}
                series={[
                    {
                    data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        if(xValue['pool_price']!==null){
                            return Number(parseFloat(xValue['pool_price']))
                        }else{
                            return null
                        }

                    }):[],
                    label: 'Pool Price ($)',
                    color: '#de3c64',
                    showMark: true,
                },
                ...buildPoolPriceModelSeries(stfdata, true),
                {
                    data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        if(xValue['aeso_forecast_price'] !== null){
                            return Number(parseFloat(xValue['aeso_forecast_price']))
                        }else{
                            return null
                        }
                        
                    }):[],
                    label: 'AESO Forecast Price ($)',
                    color: '#545454', 
                    showMark: true,
                },
                {
                    data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        const hasForecastValue = pickFirstChartNumber(xValue, [
                            'rf_24h_specialists_forecast_price',
                            'aeso_forecast_price',
                        ]);
                        if (hasForecastValue !== null){
                            return marginalCostValue
                        }else{
                            return null
                        }
                            
                    }):[],
                    label: 'Marginal Cost ($)',
                    color: '#5dd0ff', 
                    showMark: false,
                },
                ]}
                />
            </Box>  
            </Box>
            )}
            {selectedCharts.short_forecast_store_models && stfStoreData.length > 0 && role === "dev" && (
                <Box className='graph'>
                <p className='Title'>Short Term Forecast - T + 3 Store Models</p>
                <Box className='graph-inner'>
                <LineChart
                margin={{ left: 80}}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '10px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        padding: 1,
                    },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HE)',
                    scaleType: 'time',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: stfStoreData.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }),
                    min: stfStoreDateBounds.min,
                    max: stfStoreDateBounds.max,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Pool Price (CAD $)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: undefined,
                }]}
                series={[
                    {
                    data: stfStoreData.map((xValue, index) => {
                        if(xValue['pool_price']!==null){
                            return Number(parseFloat(xValue['pool_price']))
                        }else{
                            return null
                        }
                    }),
                    label: 'Pool Price ($)',
                    color: '#de3c64',
                    showMark: true,
                },
                ...buildStorePoolPriceModelSeries(stfStoreData, true),
                {
                    data: stfStoreData.map((xValue, index) => {
                        if(xValue['aeso_forecast_price'] !== null){
                            return Number(parseFloat(xValue['aeso_forecast_price']))
                        }else{
                            return null
                        }
                    }),
                    label: 'AESO Forecast Price ($)',
                    color: '#545454',
                    showMark: true,
                },
                {
                    data: stfStoreData.map((xValue, index) => {
                        const hasStoreForecastValue = pickFirstChartNumber(xValue, [
                            'rf_24h_specialists_store_forecast_price',
                        ]);
                        if (hasStoreForecastValue !== null){
                            return marginalCostValue
                        }else{
                            return null
                        }
                    }),
                    label: 'Marginal Cost ($)',
                    color: '#5dd0ff',
                    showMark: false,
                },
                ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.pool_price && (
            <Box className='graph'> {/*Pool Price*/}
                <p className='Title'>Pool Price</p>
                <Box className='graph-inner'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 1,
                        labelStyle: {
                            fontSize: '15px',
                            letterSpacing: '0.1em',
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                }}}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HE)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: hdata !== undefined ? hdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: hdataDateBounds.min, // Start date
                    max: hdataDateBounds.max, // End date
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                    fontWeight: 'Bold',
                    fontSize: '15px',
                    fontFamily: 'Tahoma',
                    letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(hdata) ? hdata : []).map(v => parseFloat(v['pool_price']) ?? 0)) * 1.1),
                }]}
                series={[{
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['pool_price']))
                    }):[],
                    label: 'Pool Price ($)',
                    color: '#de3c64',
                    showMark: false,
                },
                {
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['aeso_forecast_price']))
                    }):[],
                    label: 'AESO Forecast Price ($)',
                    color: '#545454',
                    showMark: false,
                },
                ...buildPoolPriceModelSeries(hdata, false),
            ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.pool_price_store_models && role === "dev" && (
            <Box className='graph'>
                <p className='Title'>Pool Price - T+3 Store Models</p>
                <Box className='graph-inner'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 1,
                        labelStyle: {
                            fontSize: '15px',
                            letterSpacing: '0.1em',
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                }}}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HE)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: hdata !== undefined ? hdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: hdataDateBounds.min,
                    max: hdataDateBounds.max,
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                    fontWeight: 'Bold',
                    fontSize: '15px',
                    fontFamily: 'Tahoma',
                    letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(hdata) ? hdata : []).map(v => parseFloat(v['pool_price']) ?? 0)) * 1.1),
                }]}
                series={[{
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['pool_price']))
                    }):[],
                    label: 'Pool Price ($)',
                    color: '#de3c64',
                    showMark: false,
                },
                {
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['aeso_forecast_price']))
                    }):[],
                    label: 'AESO Forecast Price ($)',
                    color: '#545454',
                    showMark: false,
                },
                ...buildStorePoolPriceModelSeries(hdata, false),
            ]}
                />
            </Box>
            </Box>
            )}
            {/* Accuracy Distribution - commented out
            {selectedCharts.accuracy_histogram && role === "dev" && (
            <Box className='graph'>
                <p className='Title'>Accuracy Distribution</p>
                <Box className='graph-inner'>
                    {(accuracyHistogram.length === 0) ? (
                        <p>No accuracy data available</p>
                    ) : (
                        <BarChart
                        margin={{ left: 80 }}
                        slotProps={{
                            legend: {
                                direction: 'row',
                                position: { vertical: 'top', horizontal: 'middle' },
                                padding: 1,
                                labelStyle: {
                                    fontSize: '15px',
                                    letterSpacing: '0.1em',
                                },
                                itemMarkWidth: 10,
                                itemMarkHeight: 10,
                        }}}
                            xAxis={[{
                                label: 'Accuracy Range (%)',
                                labelStyle: {
                                    fontWeight: 'Bold',
                                    fontSize: '15px',
                                    fontFamily: 'Tahoma',
                                    letterSpacing: '0.1em',
                                    },
                                data: accuracyHistogram.map(item => item.range),
                                scaleType: 'band',
                            }]}
                            yAxis={[{
                                labelStyle: {
                                    fontWeight: 'Bold',
                                    fontSize: '15px',
                                    fontFamily: 'Tahoma',
                                    letterSpacing: '0.1em',
                                    },
                                label: 'Frequency',
                                min: 0,
                            }]}
                            series={[{
                                data: accuracyHistogram.map(item => item.count),
                                label: 'Count',
                                color: '#4681E1',
                            }]}
                        />
                    )}
                </Box>
            </Box>
            )}
            */}
            {selectedCharts.generation && (
            <Box className='graph'> {/*Generation*/}
                <p className='Title'>{name1}</p>
                <div>
                    <Button className='selectBox' aria-describedby={id1} variant="contained" onClick={(e) => handleClick1(e, 'Option 1')}>▼</Button>
                    <Popover
                        id={id1}
                        open={open1}
                        anchorEl={anchorEl1}
                        onClose={handleClose1}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <List component="div" aria-label="simple dropdown1">
                            {List1.map((option, index) => (
                                <ListItemButton key={index} onClick={(e) => handleClick3(e, index)}>
                                    <ListItemText primary={option} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Popover>
                </div>
                <Box className='graph-inner'>
                {selectedItem1 === 0 ? (
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: revenueTimeline,
                    min: revenueMinDate,
                    max: revenueMaxDate,
                    disableTicks: true,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Metered Volume (MWH)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(meteredVolumeSeries) ? meteredVolumeSeries : []).map(v => v ?? 0)) * 1.1),
                }]}
                series={[{
                    data: meteredVolumeSeries,
                        label: `Metered Volume ${plantLabelUpper} (MWH)`,
                    color: '#4681E1', //dark green
                    showMark: true,
                    area: true
                }]}
                /> 
            ):(
                <LineChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 0,
                            labelStyle: {
                                fontSize: '15px',
                                fontFamily: 'Tahoma',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        },
                    }}
                      
                      yAxis={[{
                        label:'Demand (MW)\n\n',
                        labelStyle: {
                            fontWeight: 'bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: 0,
                        max: Math.ceil(Math.max(1, ...(Array.isArray(demandSeries) ? demandSeries : []).map(v => parseInt(v.peakDemand) ?? 0)) * 1.1),
                      }]}
                      xAxis={[{
                        label: 'Datetime (YYYY-MM-DD HH:mm)',
                        labelStyle: {
                            fontWeight: 'bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma', // Example of adding fontFamily
                            letterSpacing: '0.1em',
                        },
                        scaleType: 'time',
                        data: demandSeries.map((dateString) => new Date(dateString.datetime)),
                        min: demandSeries.length > 0 ? new Date(demandSeries[0].datetime) : new Date(0), // Start date
                        max: demandSeries.length > 0 ? new Date(demandSeries[demandSeries.length - 1].datetime) : new Date(0), // End date
                        valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                      }]}
                      series={[
                        {
                          data: demandSeries.map((data) => parseInt(data.peakDemand)),
                          label: 'Peak Demand',
                          labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                          },
                          color: '#4681E1',
                          showMark: false
                        },
                        {
                          data: demandSeries.map((data) => Math.round(parseFloat(data.demand) * parseFloat(data.peakDemand))),
                          label: 'Forecasted Demand',
                          labelStyle: {
                            fontSize: '0.6vw',
                            fontFamily: 'Tahoma', // Example of adding fontFamily
                            letterSpacing: '0.1em',
                            color: '#333', // Example of adding color
                          },
                          color: '#545454',
                          showMark: false
                        }
                      ]}
                    />
            )}
            </Box>
            </Box>
            )}
            {/* Quantify Beta - commented out
            {selectedCharts.quantify_beta && role === "dev" && (
            <Box className='graph'>
            <p className='Title'>Quantify Beta</p>
            <Box className='back'>
            {(!chartData || chartData.length === 0) ? (
                <p>No data available</p>
            ) : (
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: chartData.map(item => new Date(item.datetime)),
                        min: new Date(chartData[0]?.datetime ?? new Date(0)),
                        max: new Date(chartData[chartData.length - 1]?.datetime ?? new Date(0)),
                        scaleType: 'band',
                        valueFormatter: (date) =>
                            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
                    }]}
                    yAxis={[{
                        label: 'Canadian Dollars ($)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: 0,
                    }]}
                    series={[
                        {
                            data: chartData.map(item => Number(item.wisdm_revenue)),
                            label: 'Wisdm Revenue',
                            color: '#de3c64',
                            showMark: false,
                        },
                        {
                            data: chartData.map(item => Number(item.customer_revenue)),
                            label: 'Customer Revenue',
                            color: '#545454',
                            showMark: false,
                        }
                        ]}
                        />
                        )}
                    </Box>
                    </Box>
                 )}
            */}
                {selectedCharts.sevenD_accuracy && role === "dev" && (
                <Box className='graph'> {/*Small Forecast*/}
                <p className='Title'>Rolling 7 Day Accuracy</p>
                <Box className='graph-inner'>
                <LineChart
                margin={{ left: 80}}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '10px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        padding: 5,
                    },

                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    scaleType: 'time',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: datetimeAccuracy != [] ? [...datetimeAccuracy].reverse(): [],
                    min: datetimeAccuracy && datetimeAccuracy.length > 0 ? [...datetimeAccuracy].reverse()[0] : new Date(0),
                    max:
                        datetimeAccuracy && datetimeAccuracy.length > 0
                        ? [...datetimeAccuracy].reverse()[datetimeAccuracy.length - 1]
                    : new Date(0),
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{
                    label:'Accuracy %\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(rollingAccuracy) ? rollingAccuracy : []).map(v => v ?? 0), ...(Array.isArray(rollingBidAccuracy) ? rollingBidAccuracy : []).map(v => v ?? 0)) * 1.1),
                }]}
                series={[
                { data: rollingAccuracy != [] ? rollingAccuracy:[], label: 'Accuracy', color: '#de3c64', showMark: true },
                { data: rollingBidAccuracy != [] ? rollingBidAccuracy:[], label: 'Bid Accuracy', color: '#3c75deff', showMark: true },
                ]}
                />
            </Box>  
            </Box>
            )}
            {selectedCharts.revenue && revenueChartRows.length > 0 && (
            <Box className='graph'> {/*Revenue*/}
                <p className='Title'>Revenue</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                    fontSize: '15px',
                    fontFamily: 'Tahoma', 
                    letterSpacing: '0.1em',
                    padding: 5,
                },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    },
                }}
                    xAxis={[{
                    
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: revenueTimeline,
                    min: revenueMinDate,
                    max: revenueMaxDate,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                   }]}
                    yAxis={[{
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(previousMonthRevenueSeries) ? previousMonthRevenueSeries : []).map(v => v ?? 0), ...(Array.isArray(currentMonthRevenueSeries) ? currentMonthRevenueSeries : []).map(v => v ?? 0)) * 1.1),
                    }]}
                    series={[
                    {
                        data: previousMonthRevenueSeries,
                        label: `${previousMonth} Revenue ${plantLabelUpper}`,
                        color: '#545454',
                        showMark: false,
                        area: true,
                    },
                    {
                        data: currentMonthRevenueSeries,
                        label: `${currentMonth} Revenue ${plantLabelUpper}`,
                        color: '#4681E1',
                        showMark: false,
                        area: true,
                    },
                    ]}
                />
            </Box>
            </Box>
            )}
           {selectedCharts.gross_profit && Array.isArray(revenueData) && revenueData.length > 0 && (
            <Box className='graph'> {/*Revenue*/}
                <p className='Title'>Gross Profit</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                    fontSize: '15px',
                    fontFamily: 'Tahoma', 
                    letterSpacing: '0.1em',
                    padding: 5,
                },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    },
                }}
                    xAxis={[{
                    
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: Array.isArray(revenueData) ? revenueData.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        }
                    }):[],
                    min: revenueMinDate, // Start date
                    max: revenueMaxDate, // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                   }]}
                    yAxis={[{
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...(Array.isArray(revenueData) ? revenueData : []).map(data => Math.max(Number(parseFloat(data['TNG']) * Math.max(parseFloat(data['poolprice']), parseFloat(marginalCost))), 0))) * 1.1),
                    }]}
                    series={[
                    {
                        data: Array.isArray(revenueData) ? revenueData.map((data) => {
                        let computedValue = Number(parseFloat(data['TNG']) * Math.max(parseFloat(data['poolprice']), parseFloat(marginalCost)))
                        return Math.max(computedValue, 0); }):[],
                        label: `Revenue ${plantLabelUpper}`,
                        color: '#4681E1',
                        showMark: false,
                        area: true,
                    },
                    ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.ST_supply && renewabledata != null && (
            <Box className='graph'> {/*Sort Term Supply*/}
                <p className='Title'>Short Term Supply</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                yAxis={[{
                    label:'Forecasted Supply (MW)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...((renewabledata?.wind ?? []).map((v, i) => (parseInt(v) || 0) + (parseInt(renewabledata?.solar?.[i]) || 0)))) * 1.1),
                }]}
                xAxis={[
                    {
                    id: 'datetime',
                    data: renewabledata !== null ? renewabledata['time'].map(dateString => {
                        if (dateString) {
                        const date = new Date(dateString);
                        return date
                        } else {
                        return null;
                        }
                    }):[],
                    min: renewabledata !== null ? new Date(renewabledata['time'][0]): new Date(0), // Start date
                    max: renewabledata !== null ? new Date(renewabledata['time'][renewabledata['time'].length -  1]): new Date(0), // End date
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                    },
                ]}
                series={[
                    {
                        id: 'wind',
                        label: 'Wind',
                        data: renewabledata !== null ? renewabledata['wind'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: true,
                        color: '#545454'
                    },
                    {
                        id: 'solar',
                        label: 'Solar',
                        data: renewabledata !== null ?renewabledata['solar'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: true,
                        color: '#4681E1',
                    },
                ]}
                />
            </Box>
            </Box>
            )}
            {/* Long Term Forecast - commented out
            {selectedCharts.LT_rockbash && rbdata != undefined && (
            <Box className='graph'>
                <p className='Title'>Long Term Forecast</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    },
                }}
                yAxis={[{
                    label:'Forecasted Pool Price (CAD $)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '13px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        lineHeight:"1.15em"
                    },
                    min: 0,
                }]}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: rbdata !== null ? rbdata.map(dateString => {
                    if (dateString['datetime']) {
                        const date = new Date(dateString['datetime']);
                        return date
                    } else {
                        return null;
                    }
                    }):[],
                    min: rbdataDateBounds.min,
                    max: rbdataDateBounds.max,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                series={[
                    ...buildRockbashModelSeries(rbdata, false),
                ]}
                />
            </Box>
            </Box>
            )}
            */}
            {selectedCharts.LT_supply && renewabledata != undefined && (
            <Box className='graph'> {/*Large Supply*/}
                <p className='Title'>Long Term Supply</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                yAxis={[{
                    label:'Forecasted Supply (MW)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: Math.ceil(Math.max(1, ...((renewabledata?.wind168 ?? []).map((v, i) => (parseInt(v) || 0) + (parseInt(renewabledata?.solar168?.[i]) || 0)))) * 1.1),
                }]}
                xAxis={[{
                    id: 'datetime',
                    data: renewabledata !== null ? renewabledata['time168'].map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: renewabledata !== null ? new Date(renewabledata['time168'][0]): new Date(0), // Start date
                    max: renewabledata !== null ? new Date(renewabledata['time168'][renewabledata['time168'].length -  1]): new Date(0), // End date
                    scaleType: 'time',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                series={[
                    {
                        id: 'wind',
                        label: 'Wind',
                        data: renewabledata !== null ? renewabledata['wind168'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: false,
                        color: '#545454'
                    },{
                        id: 'solar',
                        label: 'Solar',
                        data: renewabledata !== null ? renewabledata['solar168'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: false,
                        color: '#4681E1',
                    }
                ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.total_generation && pieChartData != [] && (
            <Box className='graph'> {/*Total Generation Pie*/}
                <p className='Title'>{name2}</p>
                <div>
                    <Button className='selectBox' aria-describedby={id2} variant="contained" onClick={handleClick2}>▼</Button>
                    <Popover
                        id={id2}
                        open={open2}
                        anchorEl={anchorEl2}
                        onClose={handleClose2}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <List component="div" aria-label="simple dropdown">
                        {List2.map((option, index) => (
                            <ListItemButton key={index} onClick={(e) => handleClick4(e, index)}>
                                <ListItemText primary={option} />
                            </ListItemButton>
                        ))}
                        </List>
                    </Popover>
                </div>
                <Box className='back'>
                {selectedItem2 === 0 ? (
                <PieChart
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'left' },
                        padding: 10,
                        labelStyle: {
                            fontSize: '9px'
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,   
                    }
                }}
                colors={['#6978A2', '#212121', '#545454','#4681E1','#3a3a3a','#2E8BC0','#274472','#189AB4']}
                series={[{
                    arcLabel: (item) => `${item.label} (${item.value})`,
                    cx: '60%',
                    cy: '60%',
                    arcLabelMinAngle: 60,
                    arcLabelRadius: "40%",
                    data: pieChartData,
                    innerRadius: '20%',
                    outerRadius: '70%',
                    paddingAngle: 2,
                    cornerRadius: 5
                }]}
                sx={{
                [`& .${pieArcLabelClasses.root}`]: {
                    fill: '#e7edf7',
                    fontWeight: 'bold',
                    fontSize: '10px',
                },
                }}
                />
            ):(series !== undefined && (<LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '12px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    },
                }}
                yAxis={[{
                    label: 'Supply (MW)\n\n',
                    labelStyle: {
                    fontWeight: 'bold',
                    fontSize: '15px',
                    fontFamily: 'Tahoma',
                    letterSpacing: '0.1em',
                    },
                    min: 0,
                    max: undefined,
                }]}
                xAxis={[
                    {
                    id: 'datetime',
                    data: xAxisData,
                    min: hcfDateBounds.min,
                    max: hcfDateBounds.max,
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                    },
                ]}
                colors={['#6978A2', '#212121', '#545454', '#4681E1', '#3a3a3a', '#2E8BC0', '#274472', '#189AB4']}
                series={series}
                />
            ))}
            </Box>
            </Box>
            )}
            {selectedCharts.market_offers_optimized && (
            <Box className='graph'>
            <p className='Title'>ETS Market Offers Submitted</p>
            <Box className='back'>
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD HH:MM)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: bidChartSeriesPoints.map(point => point.time),
                        min: bidChartSeriesPoints[0]?.time ?? new Date(0),
                        max: bidChartSeriesPoints[bidChartSeriesPoints.length - 1]?.time ?? new Date(0),
                        scaleType: 'band',
                        valueFormatter: (date) =>
                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ` +
                        `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
                    }]}
                    yAxis={[{
                        label: 'Expected Dispatch (MW)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: Number(0),
                        max : Math.ceil(Math.max(...bidChartSeriesPoints.map(point => point.value), 1) * 1.1),
                         tickMinStep: 1, 
                    }]}
                    series={[
                        {
                            data: bidChartSeriesPoints.map(point => point.value),
                            label: plantLabelUpper,
                            color: '#de3c64',
                            showMark: false,
                        }
                        ]}
                        />
                    </Box>
                    </Box>
                 )}
                 {/* {selectedCharts.mc_trigger_short_term && (
            <Box className='graph'>
            <p className='Title'>Short Term MC Trigger</p>
            <Box className='back'>
            {(!stfdata || !MEConfig.marginal_cost || stfdata.length === 0) ? (
                <p>No data available</p>
            ) : (
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD HH:MM)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: stfdata !== undefined ? stfdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    scaleType: "band",
                    min: stfDateBounds.min, // Start date
                    max: stfDateBounds.max, // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                    }]}
                    yAxis={[{
                        label: 'Triggered (True or False)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: Number(0),
                        max : Number(1),
                         tickMinStep: 1, 
                    }]}
                    series={[
                        {
                             data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        if(xValue['forecast_price']!==null){
                            if (xValue['forecast_price']>= MEConfig.marginal_cost){
                                return Number(1)
                            }else{
                                return Number(0)
                            }
                            
                        }else{
                            return null
                        }
                        
                    }):[],
                            label: plantLabelUpper,
                            color: '#de3c64',
                            showMark: false,
                        }
                        ]}
                        />
                        )}
                    </Box>
                    </Box>
                 )}
                 {selectedCharts.mc_trigger_long_term && (
            <Box className='graph'>
            <p className='Title'>Long Term MC Trigger</p>
            <Box className='back'>
            {(!rbdata || !MEConfig.marginal_cost || rbdata.length === 0) ? (
                <p>No data available</p>
            ) : (
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD HH:MM)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: rbdata !== null ? rbdata.map(dateString => {
                    if (dateString['datetime']) {
                        const date = new Date(dateString['datetime']);
                        return date
                    } else {
                        return null;
                    }
                    }):[],
                    min: rbdataDateBounds.min, // Start date
                    max: rbdataDateBounds.max, // End date\
                    scaleType: "band",
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
                    
                    
                    }]}
                    yAxis={[{
                        label: 'Triggered (True or False)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: Number(0),
                        max : Number(1),
                         tickMinStep: 1, 
                    }]}
                    series={[
                        {
                             data: rbdata !== null ? rbdata.map((xValue) => {
                            if (xValue['forecastplus_price']> MEConfig.marginal_cost){
                                return 1;
                            }else{
                                return 0;
                            }
                            
                        }):[],
                            label: plantLabelUpper,
                            color: '#de3c64',
                            showMark: false,
                        }
                        ]}
                        />
                        )}
                    </Box>
                    </Box>
                 )} */}
            {selectedCharts.current_supply_demand && supplyDemandTimeline.length > 0 && (
            <Box className='graph'>
                <p className='Title'>Historical CSD — Metered Volume {plantLabelUpper}</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' }, padding: 0, labelStyle: { fontSize: '15px', fontFamily: 'Tahoma', letterSpacing: '0.1em' }, itemMarkWidth: 10, itemMarkHeight: 10 } }}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HE)',
                    labelStyle: { fontWeight: 'Bold', fontSize: '15px', fontFamily: 'Tahoma', letterSpacing: '0.1em' },
                    scaleType: 'time',
                    data: supplyDemandTimeline,
                    min: csdMinDate,
                    max: csdMaxDate,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')} ${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`,
                }]}
                yAxis={[{
                    label: 'Metered Volume (MWH)\n\n',
                    labelStyle: { fontWeight: 'Bold', fontSize: '15px', fontFamily: 'Tahoma', letterSpacing: '0.1em' },
                    min: 0,
                    max: csdYMax,
                }]}
                series={[
                    { data: tngValues, label: `TNG ${plantLabelUpper}`, color: '#4681E1', showMark: false, area: true },
                ]}
                />
                </Box>
            </Box>
            )}
            </Box>
    );
}
